// LiveStreamUserEnteredNotice.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import LivestreamSuperChatOverlay from '../container/LivestreamSuperChatOverlay.js';
import LiveStreamUserAvatar from '../container/LiveStreamUserAvatar.js';
import UserLevelBadge from '../container/UserLevelBadge.js';
import UserLivestreamBadges from '../container/UserLivestreamBadges.js';
import { USER_BADGE_SIZE } from '../component/UserLivestreamBadges.jsx';
import Trans from '../component/Trans.jsx';

import { TranslationNamespace } from '../resource/translationNamespace.js';

import { textColor } from '../style/variables.js';
import SlideInFromLeft from '../style/SlideInFromLeft.js';
import SlideOutToLeft from '../style/SlideOutToLeft.js';

export class LiveStreamUserEnteredNotice extends React.PureComponent {
  nextTick = null;
  fetchUser = () => {
    const {
      userId,
      username,
      displayName,
      isUserFetching,
      isUserFetched,
      fetchUser,
    } = this.props;
    if (
      userId &&
      (!username || (!displayName && !isUserFetching && !isUserFetched))
    ) {
      this.nextTick = setTimeout(() => {
        fetchUser({ userId });
      });
    }
  };
  componentDidMount() {
    this.fetchUser();
  }
  componentDidUpdate(prevProps) {
    const { userId } = this.props;
    if (userId !== prevProps.userId) {
      this.fetchUser();
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }
  render() {
    const {
      t,
      noticeStatus,
      streamId,
      userId,
      displayName,
      username,
      level,
      senderBadges,
      isDisplayNameEnabled,
      vipChatStyle,
      diamonds,
      text,
    } = this.props;
    if (!userId) return null;

    const shownUsername = isDisplayNameEnabled
      ? displayName || `@${username}`
      : `@${username}`;

    if (diamonds) {
      return (
        <StyledLiveStreamUserEnteredNotice status={noticeStatus} hasMaxWidth>
          <LivestreamSuperChatOverlay
            streamId={streamId}
            userId={userId}
            text={text}
            diamonds={diamonds}
          />
        </StyledLiveStreamUserEnteredNotice>
      );
    }

    if (!level) {
      return (
        <StyledLiveStreamUserEnteredNotice status={noticeStatus}>
          <NoticeBody level={0}>
            <NoticeInfo>
              <NoticeText>
                {t('chat_vip_watch_livestream', {
                  username: shownUsername,
                  ns: TranslationNamespace.LIVESTREAM,
                })}
              </NoticeText>
            </NoticeInfo>
          </NoticeBody>
          <div />
        </StyledLiveStreamUserEnteredNotice>
      );
    }

    const backgroundColor = vipChatStyle?.['backgroundColor'];
    const borderColor = vipChatStyle?.['borderColor'];
    const borderImage = vipChatStyle?.['borderImage'];

    return (
      <StyledLiveStreamUserEnteredNotice status={noticeStatus}>
        <NoticeBody
          $backgroundColor={backgroundColor}
          $borderColor={borderColor}
          $borderImage={borderImage}
        >
          <AvatarInfo>
            <AvatarWrapper>
              <LiveStreamUserAvatar
                streamId={streamId}
                userId={userId}
                badgeSize={44}
                avatarSize={28}
                diamondSize={10}
                fontSize={16}
                shouldDisplayMVP={true}
              />
            </AvatarWrapper>
          </AvatarInfo>
          <NoticeInfo>
            <NoticeText>
              <Trans
                defaults={t('chat_vip_watch_livestream', {
                  username: shownUsername,
                  ns: TranslationNamespace.LIVESTREAM,
                }).replace(shownUsername, '<0>{username}</0>')}
                values={{ username: shownUsername }}
                components={[
                  <>
                    {shownUsername}
                    <UserLevelBadge level={level} />
                    <UserLivestreamBadges badges={senderBadges} />
                  </>,
                ]}
              />
            </NoticeText>
          </NoticeInfo>
        </NoticeBody>
        <NoticeTail
          $backgroundColor={backgroundColor}
          $borderColor={borderColor}
        >
          <TailLine $color={borderColor} />
        </NoticeTail>
      </StyledLiveStreamUserEnteredNotice>
    );
  }
}

LiveStreamUserEnteredNotice.propTypes = {
  t: PropTypes.func.isRequired,
  streamId: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  text: PropTypes.string,
  isUserFetching: PropTypes.bool,
  isUserFetched: PropTypes.bool,
  isDisplayNameEnabled: PropTypes.bool,
  level: PropTypes.number,
  diamonds: PropTypes.number,
  fetchUser: PropTypes.func,
  noticeStatus: PropTypes.string,
  senderBadges: PropTypes.array,
  vipChatStyle: PropTypes.object,
};

LiveStreamUserEnteredNotice.defaultProps = {
  streamId: '',
  userId: '',
  username: '',
  displayName: '',
  text: '',
  isUserFetching: false,
  isUserFetched: false,
  isDisplayNameEnabled: true,
  level: null,
  diamonds: 0,
  fetchUser: () => null,
  noticeStatus: 'display',
  senderBadges: [],
  vipChatStyle: {},
};

const StyledLiveStreamUserEnteredNotice = styled.div.attrs(
  ({ hasMaxWidth }) => ({
    style: {
      maxWidth: hasMaxWidth ? '328px' : 'none',
    },
  })
)`
  display: inline-flex;
  align-items: center;
  ${({ status }) => {
    if (status === 'coming') {
      return css`
        animation: 0.5s ${SlideInFromLeft};
      `;
    } else if (status === 'leaving') {
      return css`
        animation: 0.6s ${SlideOutToLeft};
      `;
    }
  }}
`;

const NoticeBody = styled.div`
  position: relative;
  display: flex;
  height: 20px;
  width: auto;
  padding-left: 16px;
  margin-left: -16px;
  border-left: none;
  ${({ $backgroundColor }) => {
    if ($backgroundColor) {
      return css`
        background-color: ${$backgroundColor};
      `;
    } else {
      return css`
        background: linear-gradient(
          270deg,
          rgba(0, 0, 0, 0) 6.5%,
          rgba(0, 0, 0, 0.4) 100%
        );
      `;
    }
  }}

  ${({ $borderColor, $borderImage }) => {
    if ($borderColor) {
      return css`
        border-top: solid 1.5px ${$borderColor};
        border-bottom: solid 1.5px ${$borderColor};

        :before {
          content: '';
          position: absolute;
          width: 90%;
          height: 1px;
          background-repeat: no-repeat;
          background-size: 100% 100%;
          background-image: url(${$borderImage});
          top: -1px;
          left: 5%;
        }
        :after {
          content: '';
          position: absolute;
          width: 90%;
          height: 1px;
          background-repeat: no-repeat;
          background-size: 100% 100%;
          background-image: url(${$borderImage});
          bottom: -1px;
          left: 5%;
        }
      `;
    }
  }}
`;

const AvatarInfo = styled.div`
  position: relative;
  width: 44px;
`;
const AvatarWrapper = styled.div`
  position: absolute;
  top: -12px;
  left: 0;
`;
const NoticeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NoticeText = styled.span`
  color: ${textColor.white};
  font-size: 12px;
  line-height: ${USER_BADGE_SIZE}px;
  text-decoration: none;
  display: flex;
  gap: 4px;
`;

const NoticeTail = styled.div`
  width: 20px;
  height: 20px;
  ${({ $backgroundColor }) => {
    if ($backgroundColor) {
      return css`
        background: linear-gradient(
            -45deg,
            transparent 13px,
            ${$backgroundColor} 0
          )
          left;
      `;
    }
  }}
  ${({ $borderColor }) => {
    if ($borderColor) {
      return css`
        border-top: solid 1.5px ${$borderColor};
      `;
    }
  }}
`;

const TailLine = styled.div`
  margin-top: -1.5px;
  width: 0;
  height: 20px;
  ${({ $color }) => ($color ? `border-left: solid 2px ${$color}` : '')};
  transform-origin: bottom;
  transform: skewX(-45deg);

  :before {
    content: ' ';
    position: absolute;
    width: 4px;
    height: 1px;
    ${({ $color }) => ($color ? `background: ${$color}` : '')};
    bottom: 0px;
    left: -4px;
  }
  :after {
    content: ' ';
    position: absolute;
    width: 4px;
    height: 1px;
    ${({ $color }) => ($color ? `background: ${$color}` : '')};
    top: 0px;
    left: -4px;
  }
`;

export default withTranslation()(LiveStreamUserEnteredNotice);
